import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  XCircle,
  Loader2,
  Play,
  RefreshCw,
  FileText,
  Radio,
  Clock,
  AlertCircle,
  ArrowLeft,
  Layers,
  Terminal,
  ExternalLink,
  FileSpreadsheet,
  StopCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Tooltip } from '../components/ui/tooltip';
import { analysisAPI, logAPI } from '../services/api';


/**
 * UnifiedLogViewer
 *
 * Inner component that renders the log panel for a batch analysis run.
 * It first checks the analysis status, then either:
 *   - Opens a WebSocket for live log streaming (if the run is pending/running), or
 *   - Falls back to fetching historical (completed/failed) logs via REST.
 *
 * If the WebSocket receives a "container not found" message, it automatically
 * falls back to historical logs (the container has already stopped).
 *
 * @param {string} parentId - The analysis ID whose logs should be displayed.
 */
/**
 * PhaseLogPanel
 *
 * Renders a single log panel for one pipeline phase (sequencing or visualization).
 *
 * - Sequencing panel: always fetches historical logs (sequencing is finished by the
 *   time the user is looking at visualization logs).
 * - Visualization panel: opens a live WebSocket when the phase is active; falls back
 *   to historical logs once the container exits.
 *
 * @param {string} parentId   - The parent analysis ID.
 * @param {string} phase      - "sequencing" or "visualization".
 * @param {boolean} isLive    - True when this phase's container is currently running.
 */
function PhaseLogPanel({ parentId, phase, isLive }) {
  const [logs, setLogs] = useState([]);
  const [historicalLogs, setHistoricalLogs] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('loading');
  const [error, setError] = useState(null);
  const logsEndRef = useRef(null);

  // Auto-scroll on new live lines.
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  // Auto-scroll when historical logs load.
  useEffect(() => {
    if (historicalLogs) logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [historicalLogs]);

  useEffect(() => {
    let ws = null;

    const fetchHistoricalLogs = async () => {
      setConnectionStatus('loading_historical');
      try {
        const response = await logAPI.getHistoricalLogs(parentId, phase);
        setHistoricalLogs(response.data.logs);
        setConnectionStatus('historical');
      } catch (err) {
        setError(err.response?.data?.detail || err.message);
        setConnectionStatus('error');
      }
    };

    const connectWebSocket = () => {
      setConnectionStatus('connecting');
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      ws = new WebSocket(`${wsProtocol}//${window.location.host}/api/v1/logs/stream/${parentId}`);
      ws.onopen = () => setConnectionStatus('live');
      ws.onmessage = (event) => {
        if (event.data.includes('container') && event.data.includes('not found')) {
          fetchHistoricalLogs();
        } else {
          setLogs(prev => [...prev, event.data]);
        }
      };
      ws.onerror = () => fetchHistoricalLogs();
      ws.onclose = () => setConnectionStatus(s => s === 'live' ? 'closed' : s);
    };

    if (isLive) {
      connectWebSocket();
    } else {
      fetchHistoricalLogs();
    }

    return () => { if (ws) ws.close(); };
  }, [parentId, phase, isLive]);

  const badge = () => {
    switch (connectionStatus) {
      case 'live':
        return <span className="flex items-center gap-1 text-green-600 text-xs font-medium px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded-full"><Radio className="h-3 w-3" /> Live</span>;
      case 'historical':
        return <span className="flex items-center gap-1 text-muted-foreground text-xs font-medium px-2 py-1 bg-muted rounded-full"><Clock className="h-3 w-3" /> Historical</span>;
      default:
        return <span className="flex items-center gap-1 text-muted-foreground text-xs font-medium px-2 py-1 bg-muted rounded-full"><Loader2 className="h-3 w-3 animate-spin" /> Loading</span>;
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium flex items-center gap-2">
          <Terminal className="h-4 w-4" />
          {phase === 'sequencing' ? 'Sequencing Logs' : 'Visualization Logs'}
        </span>
        {badge()}
      </div>
      <div className="bg-gray-900 dark:bg-gray-950 rounded-lg p-4 h-[400px] overflow-y-auto">
        {error ? (
          <div className="text-red-400 text-sm">{error}</div>
        ) : historicalLogs ? (
          <>
            <pre className="text-xs whitespace-pre-wrap break-words font-mono text-gray-200">{historicalLogs}</pre>
            <div ref={logsEndRef} />
          </>
        ) : logs.length > 0 ? (
          <div className="space-y-0.5 font-mono text-xs text-gray-200">
            {logs.map((log, index) => <div key={index}>{log}</div>)}
            <div ref={logsEndRef} />
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500 text-sm">
            {isLive ? 'Waiting for logs...' : 'No logs available yet'}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * BatchAnalysisMonitor
 *
 * Route: /analysis/:parentId/manage
 *
 * Main monitoring page for a multi-batch sequencing run. Shows:
 *   - Overall pipeline status (sequencing → visualization).
 *   - Per-batch progress bar during the sequencing phase.
 *   - Controls to re-run sequencing, start/re-run R visualization, and view results.
 *   - A unified live/historical log viewer for the entire run.
 *
 * Polls the batch status API every 10 seconds so the UI stays up to date without
 * a manual refresh.
 */
export default function BatchAnalysisMonitor() {
  const { parentId } = useParams();
  const navigate = useNavigate();

  // Full batch status object returned by the API (batches array, phase, flags, etc.).
  const [batchStatus, setBatchStatus] = useState(null);
  // True while the initial batch status fetch is in progress.
  const [loading, setLoading] = useState(true);
  // Error message shown in the red banner.
  const [error, setError] = useState('');
  // Success message shown in the green banner.
  const [success, setSuccess] = useState('');
  // Whether to include failed batches as N/A values when starting visualization.
  const [includeFailedAsNa, setIncludeFailedAsNa] = useState(false);
  // True while the "Run Visualization" request is in flight.
  const [startingVisualization, setStartingVisualization] = useState(false);
  // True while the "Re-run Sequencing" request is in flight.
  const [restarting, setRestarting] = useState(false);
  // True while the "Stop" request is in flight.
  const [stopping, setStopping] = useState(false);
  // Incremented each time sequencing is restarted so log panel keys force a remount.
  const [restartCount, setRestartCount] = useState(0);
  // Prevents auto-triggering visualization more than once per mount.
  const hasAutoTriggeredViz = useRef(false);

  // Fetches the latest batch status from the API and updates state.
  const fetchBatchStatus = async () => {
    try {
      const response = await analysisAPI.getBatchStatus(parentId);
      setBatchStatus(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch batch status');
    } finally {
      setLoading(false);
    }
  };

  // Fetch status on mount and then poll every 10 seconds.
  // The interval is cleared automatically when the component unmounts.
  useEffect(() => {
    fetchBatchStatus();
    // Poll for updates every 10 seconds
    const interval = setInterval(fetchBatchStatus, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId]);

  /**
   * Starts the R visualization step for the completed sequencing results.
   * On success, redirects to the visualization log page after a short delay.
   */
  const handleStartVisualization = async () => {
    setStartingVisualization(true);
    setError('');

    try {
      await analysisAPI.startVisualization(parentId, includeFailedAsNa);
      setSuccess('R visualization started! Generating plots...');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start visualization');
      hasAutoTriggeredViz.current = false; //allow retry on error
    } finally {
      setStartingVisualization(false);
    }
  };

  /**
   * Re-runs the full sequencing pipeline from scratch.
   * Refreshes the status display after a short delay to show the updated state.
   */
  const handleRestart = async () => {
    setRestarting(true);
    setError('');
    setSuccess('');

    try {
      await analysisAPI.restartSequencing(parentId);
      setRestartCount(c => c + 1);
      hasAutoTriggeredViz.current = false;
      setSuccess('Batch sequencing restarted! Refreshing status...');
      // Refresh the status after a short delay
      setTimeout(() => {
        fetchBatchStatus();
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to restart sequencing');
    } finally {
      setRestarting(false);
    }
  };

  /**
   * Stops whatever container is currently running (sequencing or visualization).
   */
  const handleStop = async () => {
    setStopping(true);
    setError('');
    setSuccess('');
    try {
      await analysisAPI.stopAnalysis(parentId);
      setSuccess('Analysis stopped. Use Re-run Sequencing or Re-run Visualization to resume.');
      setTimeout(() => { fetchBatchStatus(); setSuccess(''); }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to stop analysis');
    } finally {
      setStopping(false);
    }
  };

  // Pipeline state helpers — derived from the latest batchStatus response.
  const phase = batchStatus?.phase || 'sequencing';
  const parentStatus = batchStatus?.parent_status;
  const isStillProcessing = !!batchStatus && !batchStatus.all_complete && !batchStatus.ready_for_visualization && parentStatus !== 'failed';
  const isSeqFailed = !!batchStatus && !batchStatus.all_complete && !batchStatus.ready_for_visualization && parentStatus === 'failed' && phase === 'sequencing';
  const isReadyForViz = !!batchStatus?.ready_for_visualization;
  const isVizRunning = phase === 'visualization' && parentStatus === 'running';
  const isVizFailed = phase === 'visualization' && parentStatus === 'failed';
  const isVizComplete = phase === 'visualization' && parentStatus === 'completed';

  // Button enable/disable guards — prevent double-clicks and conflicting actions.
  const canRestart = !isStillProcessing && !isVizRunning && !restarting && !startingVisualization;
  const canVisualize = (isReadyForViz || isVizFailed || isVizComplete) && !startingVisualization;

  // Auto-trigger visualization once sequencing is complete.
  useEffect(() => {
    if (isReadyForViz && !hasAutoTriggeredViz.current) {
      hasAutoTriggeredViz.current = true;
      handleStartVisualization();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadyForViz]);

  /**
   * Returns the icon, background colour, title, and description for the pipeline
   * status card, based on the current phase and status combination.
   */
  const getStatusInfo = () => {
    if (isVizComplete) return {
      icon: <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />,
      bg: 'bg-green-100 dark:bg-green-900/30',
      title: 'Analysis Complete',
      titleColor: 'text-green-800 dark:text-green-200',
      desc: 'Your plots are ready to view.',
    };
    if (isVizFailed) return {
      icon: <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />,
      bg: 'bg-orange-100 dark:bg-orange-900/30',
      title: 'Plot Generation Failed',
      titleColor: 'text-orange-800 dark:text-orange-200',
      desc: "Couldn't generate plots. Check the logs for details.",
    };
    if (isVizRunning) return {
      icon: <Loader2 className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />,
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      title: 'Generating Plots',
      titleColor: 'text-blue-800 dark:text-blue-200',
      desc: "R visualization is running. You'll be redirected when done.",
    };
    if (isReadyForViz) return {
      icon: <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />,
      bg: 'bg-green-100 dark:bg-green-900/30',
      title: 'Sequencing Complete',
      titleColor: 'text-green-800 dark:text-green-200',
      desc: `${batchStatus.batches.filter(b => b.status === 'completed').length} of ${batchStatus.batches.length} batches completed. Ready to generate plots.`,
    };
    if (isSeqFailed) return {
      icon: <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />,
      bg: 'bg-red-100 dark:bg-red-900/30',
      title: 'Sequencing Failed',
      titleColor: 'text-destructive',
      desc: 'Analysis failed during sequencing. Check the logs for details.',
    };
    return {
      icon: <Loader2 className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />,
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      title: 'Processing Batches',
      titleColor: 'text-blue-800 dark:text-blue-200',
      desc: 'Sequential processing in progress. This page will auto-refresh.',
    };
  };
  const statusInfo = getStatusInfo();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12 max-w-6xl">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8"
      >
        <div>
          <Link to="/dashboard">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
        <div className="flex items-center gap-2 text-muted-foreground mb-2 mt-4">
          <Layers className="h-4 w-4" />
          <span className="text-sm">Batch Analysis #{parentId}</span>
        </div>
        <h1 className="text-3xl font-bold mb-2">Batch Monitor</h1>
      </motion.div>

      {/* Messages */}
      {success && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 p-4 rounded-lg flex items-start gap-3 mb-6"
        >
          <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <span>{success}</span>
        </motion.div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-destructive/10 border border-destructive/20 text-destructive p-4 rounded-lg flex items-start gap-3 mb-6"
        >
          <XCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </motion.div>
      )}

      {/* Pipeline Controls */}
      <motion.div
        key={`${phase}-${parentStatus}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${statusInfo.bg}`}>
                  {statusInfo.icon}
                </div>
                <div>
                  <CardTitle className={`text-base ${statusInfo.titleColor}`}>{statusInfo.title}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-0.5">{statusInfo.desc}</p>
                </div>
              </div>
              {batchStatus?.project_id && (
                <Tooltip content="View and manage sample metadata for this project" side="left">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/project/${batchStatus.project_id}/metadata`)}
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Metadata
                  </Button>
                </Tooltip>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3 pt-0">
            {/* Batch progress — only shown during sequencing; only completed/failed are truthful */}
            {phase === 'sequencing' && batchStatus?.batches?.length > 0 && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>
                    {batchStatus.batches.filter(b => b.status === 'completed').length} of {batchStatus.batches.length} batches complete
                  </span>
                  {batchStatus.any_failed && (
                    <span className="text-red-500">
                      {batchStatus.batches.filter(b => b.status === 'failed').length} failed
                    </span>
                  )}
                </div>
                <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden flex">
                  <div
                    className="h-full bg-green-500 transition-all duration-700"
                    style={{ width: `${(batchStatus.batches.filter(b => b.status === 'completed').length / batchStatus.batches.length) * 100}%` }}
                  />
                  {batchStatus.any_failed && (
                    <div
                      className="h-full bg-red-400 transition-all duration-700"
                      style={{ width: `${(batchStatus.batches.filter(b => b.status === 'failed').length / batchStatus.batches.length) * 100}%` }}
                    />
                  )}
                </div>
                {/* Per-batch status chips */}
                <div className="flex flex-wrap gap-2 pt-1">
                  {batchStatus.batches.map((batch) => {
                    const isRunning = batch.status === 'running' || batch.status === 'pending';
                    const isCompleted = batch.status === 'completed';
                    const isFailed = batch.status === 'failed';
                    return (
                      <span
                        key={batch.batch_number}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${isRunning ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800' :
                          isCompleted ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800' :
                            isFailed ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800' :
                              'bg-muted text-muted-foreground border-transparent'
                          }`}
                      >
                        {isRunning && <Loader2 className="h-3 w-3 animate-spin" />}
                        {isCompleted && <CheckCircle className="h-3 w-3" />}
                        {isFailed && <XCircle className="h-3 w-3" />}
                        {!isRunning && !isCompleted && !isFailed && <Clock className="h-3 w-3" />}
                        Batch {batch.batch_number}
                        {isRunning && ' — Processing'}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
            {(isReadyForViz || isVizFailed) && batchStatus?.any_failed && (
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200">
                {batchStatus.batches.filter(b => b.status === 'failed').length} batch(es) failed. You can proceed with successful batches or restart sequencing.
              </div>
            )}
            {(isReadyForViz || isVizFailed) && batchStatus?.any_failed && (
              <div className="flex items-center gap-2">
                <input type="checkbox" id="include-failed" checked={includeFailedAsNa} onChange={(e) => setIncludeFailedAsNa(e.target.checked)} className="rounded" />
                <label htmlFor="include-failed" className="text-sm">Include failed batches as N/A</label>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t">
              {/* Re-run group — both pipeline re-run actions together */}
              <div className="flex items-center gap-2">
                <Tooltip content="Re-run the entire sequencing pipeline from scratch">
                  <Button
                    onClick={handleRestart}
                    disabled={!canRestart}
                    variant="outline"
                    size="sm"
                  >
                    {restarting
                      ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Running...</>
                      : <><RefreshCw className="mr-2 h-4 w-4" />Re-run Sequencing</>}
                  </Button>
                </Tooltip>
                <Tooltip content={isVizComplete || isVizFailed ? 'Re-run R visualization on the existing sequencing results' : 'Generate plots and reports from completed sequencing results'}>
                  <Button
                    onClick={handleStartVisualization}
                    disabled={!canVisualize}
                    variant="outline"
                    size="sm"
                  >
                    {startingVisualization
                      ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{isVizComplete || isVizFailed ? 'Restarting...' : 'Generating...'}</>
                      : isVizComplete || isVizFailed
                        ? <><RefreshCw className="mr-2 h-4 w-4" />Re-run Visualization</>
                        : <><Play className="mr-2 h-4 w-4" />Run Visualization</>}
                  </Button>
                </Tooltip>
                {(isStillProcessing || isVizRunning) && (
                  <Tooltip content="Stop the currently running sequencing or visualization container">
                    <Button
                      onClick={handleStop}
                      disabled={stopping}
                      variant="outline"
                      size="sm"
                      className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                    >
                      {stopping
                        ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Stopping...</>
                        : <><StopCircle className="mr-2 h-4 w-4" />Stop</>}
                    </Button>
                  </Tooltip>
                )}
              </div>
              {/* Results — visually distinct primary action */}
              {isVizComplete && (
                <Tooltip content="View interactive plots and analysis results" side="left">
                  <Button
                    onClick={() => navigate(`/analysis/${parentId}/results`, {
                      state: { backTo: `/analysis/${parentId}/manage`, backLabel: 'Manage' }
                    })}
                    size="sm"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Results
                  </Button>
                </Tooltip>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Logs — sequencing and visualization shown as separate panels */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Logs
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <PhaseLogPanel
            parentId={parentId}
            phase="sequencing"
            isLive={phase === 'sequencing' && (parentStatus === 'running' || parentStatus === 'pending')}
            key={`sequencing-${restartCount}`}
          />
          {(phase === 'visualization' || batchStatus?.batches?.some(b => b.status === 'completed')) && (
            <PhaseLogPanel
              parentId={parentId}
              phase="visualization"
              isLive={phase === 'visualization' && parentStatus === 'running'}
              key={`visualization-${restartCount}`}
            />
          )}
        </CardContent>
      </Card>

    </div>
  );
}
